import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';

export class RouteStrategy implements RouteReuseStrategy {
  storedRouteHandles = new Map<string, DetachedRouteHandle>();

  // Clear all cache
  clear(): void {
    if (this.storedRouteHandles.size === 0) {
      return;
    }

    this.storedRouteHandles.forEach((handle) => {
      if (handle && typeof handle === 'object') {
        const componentRef = (handle as any).componentRef;
        if (
          componentRef &&
          typeof componentRef.instance?.ngOnDestroy === 'function'
        ) {
          componentRef.instance.ngOnDestroy();
        }
      }
    });
    this.storedRouteHandles.clear();
  }

  // Decides if the route should be stored
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const key = this.getKey(route);
    return !!key;
  }

  getKey(route: ActivatedRouteSnapshot): string {
    return (route.routeConfig.data as any)?.routeCacheKey;
  }

  //Store the information for the route we're destructing
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    this.storedRouteHandles.set(this.getKey(route), handle);
  }

  //Return true if we have a stored route object for the next route
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return this.storedRouteHandles.has(this.getKey(route));
  }

  //If we returned true in shouldAttach(), now return the actual route data for restoration
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle {
    return this.storedRouteHandles.get(this.getKey(route));
  }

  //Reuse the route if we're going to and from the same route
  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot,
  ): boolean {
    if (future.routeConfig !== curr.routeConfig) {
      return false;
    }

    // Claim pages must fully re-initialize when switching to a different claim key.
    // Detect this route by the presence of the ClaimResolver ("claim"), instead of hardcoding the route path.
    const isClaimRoute = !!future.routeConfig?.resolve?.['claim'];
    if (isClaimRoute) {
      return (
        future.paramMap.get('recordSource') === curr.paramMap.get('recordSource') &&
        future.paramMap.get('recordId') === curr.paramMap.get('recordId')
      );
    }

    return true;
  }
}
